import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FixedSizeList } from 'react-window';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { Team, User } from 'api/models';
import { Loading } from 'components/atoms/loading';
import { SquadData } from 'components/molecules/squad-picker/use-squad-options';
import { useCKMentionEvents } from 'hooks/use-ck-mention-events';
import { fullName } from 'utils/full-name';
import { getUserLabel } from 'utils/get-user-label';
import { MentionType } from 'vendor/ckeditor/plugins/mention/types';
import { SuggestionComponentProps } from './mention-popup-content';
import { setActiveSuggestion, useMentions } from './mention-store';
import { SuggestionListItem } from './suggestion-list-item';
import {
  generateSuggestionItem,
  isFirstNameMatching,
  isItemMatching,
  isLastNameMatching,
} from './utils';

interface MembersMentionProps extends SuggestionComponentProps {
  data?: SquadData;
  isLoading?: boolean;
}

export const MembersSuggestions: FC<MembersMentionProps> = ({
  onSelect,
  query,
  data,
  activeTab,
  isLoading = false,
}) => {
  const inProjectUsers = data?.users;
  const notInProjectUsers = data?.notInProject;
  const userList = useMemo(
    () => [...(inProjectUsers || []), ...(notInProjectUsers || [])],
    [inProjectUsers, notInProjectUsers],
  );
  const notInProjectUserIds = (data?.notInProject ?? []).map((u) => u.userId);
  const teamList = data?.teams || [];

  const [contextState, contextDispatch] = useMentions();
  const { activeSuggestion, editor } = contextState;

  const [userDataSource, setUserDataSource] = useState<User[]>([]);
  const [teamDataSource, setTeamDataSource] = useState<Team[]>([]);

  const selectSuggestion = useCallback(
    (directionOffset = 1) => {
      if (activeTab !== MentionType.User) {
        // Don't handle suggestion if not in active tab
        // As there's no Teams tab, this check is suffice
        return;
      }
      // Select first/last suggestion based on the direction
      const fallbackUserOffset =
        directionOffset > 0 ? 0 : userDataSource.length - 1;
      const fallbackTeamOffset =
        directionOffset > 0 ? 0 : teamDataSource.length - 1;

      // Select first user if no suggestion is selected if dir is down and vice versa
      if (
        !activeSuggestion ||
        (activeSuggestion.type !== MentionType.User &&
          activeSuggestion.type !== MentionType.Team)
      ) {
        let suggestionItem;

        if (userDataSource[fallbackUserOffset]) {
          suggestionItem = generateSuggestionItem({
            data: userDataSource,
            index: fallbackUserOffset,
            type: MentionType.User,
          });
        } else if (teamDataSource[fallbackTeamOffset]) {
          suggestionItem = generateSuggestionItem({
            data: teamDataSource,
            index: fallbackTeamOffset,
            type: MentionType.Team,
          });
        }

        contextDispatch(setActiveSuggestion(suggestionItem));
      } else if (activeSuggestion?.type === MentionType.User) {
        // Select next user if present
        const currentActiveSuggPosition = userDataSource.findIndex(
          (item) => item.userId === activeSuggestion?.objectId,
        );

        if (userDataSource[currentActiveSuggPosition + directionOffset]) {
          const suggestionItem = generateSuggestionItem({
            data: userDataSource,
            index: currentActiveSuggPosition + directionOffset,
            type: MentionType.User,
          });

          contextDispatch(setActiveSuggestion(suggestionItem));
        } else if (teamDataSource[fallbackTeamOffset]) {
          // Select next team if no next user present
          const suggestionItem = generateSuggestionItem({
            data: teamDataSource,
            index: fallbackTeamOffset,
            type: MentionType.Team,
          });

          contextDispatch(setActiveSuggestion(suggestionItem));
        }
      } else if (activeSuggestion?.type === MentionType.Team) {
        const currentActiveSuggPosition = teamDataSource.findIndex(
          (item) => item.teamId === activeSuggestion?.objectId,
        );

        if (teamDataSource[currentActiveSuggPosition + directionOffset]) {
          const suggestionItem = generateSuggestionItem({
            data: teamDataSource,
            index: currentActiveSuggPosition + directionOffset,
            type: MentionType.Team,
          });

          contextDispatch(setActiveSuggestion(suggestionItem));
        } else if (userDataSource[fallbackUserOffset]) {
          const suggestionItem = generateSuggestionItem({
            data: userDataSource,
            index: fallbackUserOffset,
            type: MentionType.User,
          });

          contextDispatch(setActiveSuggestion(suggestionItem));
        }
      }
    },
    [
      activeSuggestion,
      activeTab,
      contextDispatch,
      teamDataSource,
      userDataSource,
    ],
  );

  useDeepCompareEffect(() => {
    if (activeTab !== MentionType.User) {
      return;
    }

    const firstNameMatches: User[] = [];
    const lastNameMatches: User[] = [];

    userList?.forEach((item) => {
      const name = fullName(item);

      if (isFirstNameMatching(name, query)) {
        firstNameMatches.push(item);
      } else if (isLastNameMatching(name, query)) {
        lastNameMatches.push(item);
      }
    });

    /**
     * first name matches has to be prioritized first over last name matches
     * hence there is separate match sets to display accordingly
     */
    const filteredUsers = [...firstNameMatches, ...lastNameMatches];

    const filteredTeams = teamList?.filter((item) =>
      isItemMatching(item.teamName, query),
    );

    setUserDataSource(filteredUsers);
    setTeamDataSource(filteredTeams);

    let suggestionItem;

    if (filteredUsers[0]) {
      suggestionItem = generateSuggestionItem({
        data: filteredUsers,
        index: 0,
        type: MentionType.User,
      });
    } else if (filteredTeams[0]) {
      suggestionItem = generateSuggestionItem({
        data: filteredTeams,
        index: 0,
        type: MentionType.User,
      });
    }

    if (suggestionItem) {
      contextDispatch(setActiveSuggestion(suggestionItem));
    }
  }, [activeTab, contextDispatch, query, userList, teamList]);

  useEffect(() => {
    if (
      activeTab === MentionType.User &&
      activeSuggestion?.type !== MentionType.User &&
      activeSuggestion?.type !== MentionType.Team
    ) {
      selectSuggestion(1);
    }
  }, [selectSuggestion, activeSuggestion, userList, activeTab]);

  const handleCKMentionEvents = useCallback(
    (e) => {
      if (e.name === 'mentionPopup:selectNext') {
        selectSuggestion(1);
      }
      if (e.name === 'mentionPopup:selectPrevious') {
        selectSuggestion(-1);
      }
    },
    [selectSuggestion],
  );

  useCKMentionEvents(handleCKMentionEvents, editor);

  const mentionItemSource = useMemo(
    () => [...userDataSource, ...teamDataSource],
    [teamDataSource, userDataSource],
  );

  return (
    <div>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <FixedSizeList
            itemCount={mentionItemSource.length}
            itemSize={40}
            height={258}
            width="100%"
          >
            {({ index, style }) => {
              const mentionItem = mentionItemSource[index];

              if ('userId' in mentionItem) {
                return (
                  <div style={style}>
                    <SuggestionListItem
                      photoUrl={mentionItem.photoUrl}
                      name={getUserLabel(mentionItem)}
                      objectId={mentionItem.userId}
                      logoUrl={mentionItem?.company?.companyLogoUrl}
                      type={MentionType.User}
                      onSelect={onSelect}
                      isNotInvited={notInProjectUserIds.includes(
                        mentionItem.userId,
                      )}
                      timeOffArray={mentionItem.userTimeOffDetails}
                    />
                  </div>
                );
              }

              return (
                <div style={style}>
                  <SuggestionListItem
                    name={mentionItem.teamName}
                    objectId={mentionItem.teamId}
                    type={MentionType.Team}
                    onSelect={onSelect}
                  />
                </div>
              );
            }}
          </FixedSizeList>
        </>
      )}
    </div>
  );
};
